import { useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {LayoutDashboard, User, Briefcase, BookOpen, Award, History, AlertTriangle, Bell, LogOut, Menu, Star, Search, Link2, X, GraduationCap} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'
import { useUnreadNotifications } from '@/hooks/useUnreadNotifications'

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', to: '/colaborador/dashboard' },
  { icon: User, label: 'Mi perfil', to: '/colaborador/perfil' },
  { icon: Search, label: 'Buscar empleos', to: '/colaborador/empleos' },
  { icon: Briefcase, label: 'Mis postulaciones', to: '/colaborador/postulaciones' },
  { icon: Link2, label: 'Vinculaciones', to: '/colaborador/vinculaciones' },
  { icon: Star, label: 'Evaluaciones', to: '/colaborador/evaluaciones' },
  { icon: History, label: 'Historial laboral', to: '/colaborador/historial' },
  { icon: GraduationCap, label: 'Cursos', to: '/colaborador/cursos' },
  { icon: BookOpen, label: 'Capacitaciones', to: '/colaborador/capacitaciones' },
  { icon: Award, label: 'Certificados', to: '/colaborador/certificados' },
  { icon: AlertTriangle, label: 'Reportes', to: '/colaborador/reportes' },
  { icon: Bell, label: 'Notificaciones', to: '/colaborador/notificaciones' },
]

interface ColaboradorLayoutProps {
  title: string
  subtitle?: string
  children: ReactNode
}

function ColaboradorLayout({ title, subtitle, children }: ColaboradorLayoutProps) {
  const { user } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { unread } = useUnreadNotifications('colaboradores', user?.userId)

  const handleLogout = async () => {
    await lumi.auth.signOut()
    window.location.href = '/'
  }

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#f8fafc' }}>
      {/* Overlay móvil */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col transition-transform md:static md:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ background: '#0d1421', borderRight: '1px solid rgba(255,255,255,0.07)' }}
      >
        {/* Logo superior */}
        <div className="flex h-16 items-center justify-between px-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <Link to="/colaborador/dashboard" onClick={() => setSidebarOpen(false)}>
            <img
              src="https://static.lumi.new/93/93deb8f4c6e571fa108d99af77ea2979.webp"
              alt="Beeznz Work"
              className="h-8 w-auto"
            />
          </Link>
          <button
            className="md:hidden text-slate-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={18} />
          </button>
        </div>

        {/* Navegación */}
        <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-0.5" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {navItems.map(({ icon: Icon, label, to }) => {
            const active = location.pathname === to
            const showNotif = to === '/colaborador/notificaciones' && unread > 0
            return (
              <Link
                key={to}
                to={to}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-white/10 text-white'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <span className="relative">
                  <Icon size={17} className={active ? 'text-amber-400' : ''} />
                  {showNotif && (
                    <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-[#0d1421]" />
                  )}
                </span>
                <span className="flex-1">{label}</span>
                {showNotif && (
                  <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold leading-none text-white">
                    {unread > 99 ? '99+' : unread}
                  </span>
                )}
              </Link>
            )
          })}
        </nav>

        {/* Logo pie + Cerrar sesión */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="px-4 py-4 flex items-center justify-center">
            <img
              src="https://static.lumi.new/d5/d5398fcebcce92c05914f918c9cc339e.webp"
              alt="Powered by Beeznz"
              className="h-10 w-auto opacity-80"
            />
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 px-5 py-4 text-sm font-semibold transition-colors hover:bg-white/5"
              style={{ color: '#f87171' }}
            >
              <LogOut size={17} />
              Cerrar sesión
            </button>
          </div>
        </div>
      </aside>

      {/* Contenido principal */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Área de contenido */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-6">
          <div className="mb-6 flex items-start gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden mt-1 rounded-lg p-1.5 text-gray-500 hover:text-gray-900 flex-shrink-0"
            >
              <Menu size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
              {subtitle ? <p className="mt-1 text-sm text-gray-500">{subtitle}</p> : null}
            </div>
          </div>
          {children}
        </main>
      </div>
    </div>
  )
}

export default ColaboradorLayout
